import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  isDevMode,
  NgZone,
  provideAppInitializer
} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  BaseRouteReuseStrategy,
  PreloadAllModules,
  provideRouter,
  RouteReuseStrategy,
  TitleStrategy,
  withComponentInputBinding,
  withPreloading,
  withRouterConfig,
  withViewTransitions
} from '@angular/router';

import {routes, stripQueryParamsGuard} from './app.routes';
import {provideAnimations} from "@angular/platform-browser/animations";
import {provideHttpClient, withFetch, withInterceptors} from "@angular/common/http";
import {authInterceptor} from "./auth.interceptor";
import {versionInterceptor} from "./version.interceptor";
import {providePrimeNG} from "primeng/config";
import {MyPreset} from "./theme";
import {GlobalErrorHandle} from "./global-error-handler";
import {ConfirmationService, MessageService} from "primeng/api";
import {PushNotificationService} from "./services/push-notification.service";
import {KillSwitchService} from "./services/kill-switch/kill-switch.service";
import {DialogService} from "primeng/dynamicdialog";
import {ApplicationService} from "./services/application.service";
import {AnalyticsService} from "./services/analytics.service";
import {provideServiceWorker} from "@angular/service-worker";
import {environment} from "../../environments/environment";
import {SignupFormService} from "./pages/signup/signup-form.service";
import {PaymentService} from "./services/payment/payment.service";
import {TitleAndDescriptionStrategy} from "./title-strategy";
import {BackNavigationService} from "./services/back-navigation.service";
import {provideCountdown} from "ngx-countdown";
import {ABService} from "./services/a-b.service";
import {provideNgxStripe} from "ngx-stripe";
import {provideClientHydration, withEventReplay, withNoHttpTransferCache} from '@angular/platform-browser';
import {ClientErrorLoggingService} from "./services/client-error-logging.service";
import {retryGetInterceptor} from "./core/retry-get.interceptor";
import {ResumeService} from "./services/resume.service";
import {App, URLOpenListenerEvent} from "@capacitor/app";
import {Capacitor} from "@capacitor/core";
import {isNative} from "./utils";
import {TranslocoHttpLoader} from './transloco-loader';
import {provideTransloco} from '@jsverse/transloco';
import {TranslateService} from "./services/translate.service";

const hydrationProviders = !isNative
  ? [ provideClientHydration(withEventReplay(), withNoHttpTransferCache()) ]
  : [];

export class CustomReuseStrategy extends BaseRouteReuseStrategy {
  override shouldReuseRoute(
    future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot
  ): boolean {
    return future.routeConfig === curr.routeConfig && JSON.stringify(future.params) === JSON.stringify(curr.params);
  }
}

export const appConfig: ApplicationConfig = {
    providers: [
      MessageService,
      DialogService,
      ConfirmationService,
      {provide: ErrorHandler, useClass: GlobalErrorHandle},
      provideCountdown({format: `hh:mm:ss`}),
      provideAppInitializer(
        () => inject(ClientErrorLoggingService).initialize(),
      ),
      provideAppInitializer(
        () => {
          provideAppInitializer(() => {
            const zone = inject(NgZone);
            const analytics = inject(AnalyticsService);

            let lastHandled: string | null = null;

            const handleUrl = async (rawUrl: string) => {
              if (rawUrl === lastHandled) return;
              lastHandled = rawUrl;

              try {
                const platform = Capacitor.getPlatform();
                const prefix = platform === 'ios' ? 'capacitor://' : 'https://';

                window.location.replace(
                  rawUrl.toString()
                    .replace(environment.baseUrl, 'https://')
                    .replace('io.myhomey.app://', prefix)
                );
                setTimeout(() => lastHandled = null, 5000);
              } catch (e) {
                console.warn('Could not parse incoming URL', e);
              }
            };

            // 1) listener (warm/resume)
            App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
              zone.run(() => { void handleUrl(event.url); });
            });

            // 2) cold start (initial launch url) + safe against duplicates
            void App.getLaunchUrl().then((launch) => {
              if (launch?.url) {
                zone.run(() => { void handleUrl(launch.url); });
              }
            });
          });
        }),
      provideAppInitializer(
        () => inject(ABService).initialize(),
      ),
      provideAppInitializer(
        () => inject(ResumeService).initialize(),
      ),
      provideAppInitializer(
        () => inject(ApplicationService).initialize(),
      ),
      provideAppInitializer(
        () => inject(KillSwitchService).initialize(),
      ),
      provideAppInitializer(
        () => inject(PushNotificationService).initialize(),
      ),
      provideAppInitializer(
        () => inject(BackNavigationService).initialize(),
      ),
      provideAppInitializer(
        () => inject(AnalyticsService).initialize(),
      ),
      provideAppInitializer(
        () => inject(PaymentService).initialize(),
      ),
      provideAppInitializer(
        () => inject(SignupFormService).initialize(),
      ),
      provideAppInitializer(
        () => inject(TranslateService).waitUntilLanguageLoaded(),
      ),
      provideNgxStripe(),
      provideRouter(
        [{
          path: '',
          canActivateChild: [stripQueryParamsGuard],
          children: routes,
        }],
        withRouterConfig({
          onSameUrlNavigation: 'reload',
        }),
        withComponentInputBinding(),
        withPreloading(PreloadAllModules),
        withViewTransitions({ skipInitialTransition: true })
      ),
      {provide: TitleStrategy, useClass: TitleAndDescriptionStrategy},
      providePrimeNG(
        {
          theme: {
            preset: MyPreset,
            options: {
              darkModeSelector: false,
            }
          }
        }
      ),
      provideAnimations(),
      provideServiceWorker('ngsw-worker.js', {
        enabled: environment.supportPWA && !isNative,
        registrationStrategy: 'registerWhenStable:30000'
      }),
      provideHttpClient(withInterceptors([authInterceptor, retryGetInterceptor, versionInterceptor]), withFetch()),
      hydrationProviders,
      {provide: RouteReuseStrategy, useClass: CustomReuseStrategy},
      provideTransloco({
        config: {
          availableLangs: ['en', 'es'],
          defaultLang: 'es',
          // Remove this option if your application doesn't support changing language in runtime.
          reRenderOnLangChange: true,
          prodMode: !isDevMode(),
        },
        loader: TranslocoHttpLoader
      })
    ]
  }
;
